import React, { useState, useCallback } from 'react';
import { Shield, RefreshCw, Loader2, FileText } from 'lucide-react';
import { SecurityMetrics, SecurityMetricsData } from '@/components/molecules/security/SecurityMetrics';
import { SecurityScannerContent } from '@/components/organisms/security/SecurityScannerContent';
import { useSecurityHealth, useSecurityFilters, useSecurityFindings, useSecurityScan, useSecurityOngoingJob, useSecurityReportPdf } from '@/hooks/security-scanner';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrg } from '@/hooks/org/useOrg';
import { useActivityStore } from '@/stores/activityStore';
import { buildImproveWithAIPromptForMetadataItem, type SecurityFindingsResponse } from '@/services/security-scanner';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import { useNavigate } from 'react-router-dom';
import type { MetadataItem } from '@/components/atoms/security/MetadataItemRow';
import { useChatPendingInputStore } from '@/stores/chatPendingInputStore';
import { useMultiAgentIdeStore } from '@/pages/multi-agent/ide/store/multiAgentIdeStore';
import { getMetadataItem } from '@/pages/multi-agent/ide/panels/resolveDependencyItemForEditor';

// Fallback metrics data
const fallbackMetrics: SecurityMetricsData = {
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  info: 0,
  overall_security_score: 0
};

export const SecurityScanner: React.FC = () => {
  const { mutateAsync: fetchReportPdf, isPending: isExporting } = useSecurityReportPdf();

  // Pagination and filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get user and organization context from authStore and orgStore
  const { user } = useAuth();
  const { selectedOrg } = useOrg();
  
  // Get the selected organization's internal UUID (not salesforceOrgId)
  const orgUuid = selectedOrg?.id || '';
  
  // Security scan hook - SSE connection persists across route changes via Zustand store
  const { startScan, isScanning, progress } = useSecurityScan({
    org_id: orgUuid,
    user_id: user?.id || '',
  });

  // Disable Scan until we know there is no ongoing job (avoids starting duplicate)
  const { isLoading: isLoadingOngoingJob } = useSecurityOngoingJob({
    org_id: orgUuid || undefined,
    user_id: user?.id,
  });

  // Disable Scan when any activity (sync, deployment, etc.) is running
  const hasAnyActivityRunning = useActivityStore((state) => state.isAnyActivityRunning());
  
  // Fetch security health data from API
  const { data: securityHealthResponse } = useSecurityHealth({
    org_id: orgUuid || undefined,
    user_id: user?.id,
  });

  // Fetch security filters from API
  const { data: securityFiltersResponse } = useSecurityFilters({
    org_id: orgUuid || undefined,
    user_id: user?.id,
  });

  // Fetch security findings with pagination and filters
  const { data: securityFindingsData, isFetching: isLoadingFindings } = useSecurityFindings({
    org_id: orgUuid || undefined,
    user_id: user?.id,
    page: currentPage,
    pageSize: pageSize,
    severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    search: searchTerm || undefined,
  });
  
  // Type assertion for the response
  const securityFindingsResponse = securityFindingsData as SecurityFindingsResponse | undefined;

  // Use API data if available, otherwise fall back to fallback data
  const metricsToDisplay: SecurityMetricsData = securityHealthResponse?.security_health || fallbackMetrics;

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSeverityChange = (severity: string) => {
    setSelectedSeverity(severity);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Refresh handler - activity tracking is handled by the store
  const handleRefresh = () => {
    startScan();
  };

  const navigate = useNavigate();
  const setMultiAgentIntent = useChatPendingInputStore((s) => s.setMultiAgentIntent);

  const handleImproveWithAI = useCallback(async (item: MetadataItem) => {
    // Seed the multi-agent with the file's DB UUID + content so the chat loads it
    // DIRECTLY instead of re-searching by name (token waste). The UUID is what lets
    // the agent skip the entity search; mirrors the SF-releases handoff.
    const id = item.metadata_item_id;
    if (id) {
      let content = "";
      try {
        const full = await getMetadataItem(id, undefined, true);
        content = full?.content || "";
      } catch { /* seed without content — agent can still load by UUID */ }
      useMultiAgentIdeStore.getState().setPendingRefactorFile({
        id,
        name: item.file_name,
        content,
        metadataType: item.metadata_type || "ApexClass",
      });
    }
    setMultiAgentIntent(buildImproveWithAIPromptForMetadataItem(item));
    navigate("/weave");
  }, [navigate, setMultiAgentIntent]);

  // Get response with hierarchical data
  const metadataItems = securityFindingsResponse?.metadataItems || [];
  const totalFindings = securityFindingsResponse?.pagination?.totalFindings || 0;
  const totalMetadataItems = securityFindingsResponse?.pagination?.totalMetadataItems || metadataItems.length;
  const totalPages = securityFindingsResponse?.pagination?.totalPages || 1;
  const startItem = totalMetadataItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalMetadataItems);

  return (
    <div className="w-full max-w-none flex flex-col h-full space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0 flex-wrap gap-y-2">
        <div>
          <h1 className="text-page-title flex items-center gap-2">
            <Shield className="h-8 w-8 text-primary" />
            Security Scanner
          </h1>
          <p className="text-subtitle">Identify and resolve security vulnerabilities in your Salesforce org</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        {/* Scan Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isScanning || isLoadingOngoingJob || hasAnyActivityRunning}
          className="gap-2"
        >
          {isScanning ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Scanning {progress}%</span>
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              <span>Run fresh scan</span>
            </>
          )}
        </Button>

        {/* Export PDF */}
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            if (!orgUuid) { toast.error("No org selected"); return; }
            try {
              const blob = await fetchReportPdf({ org_id: orgUuid, user_id: user?.id || '' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "security-report.pdf";
              a.click();
              URL.revokeObjectURL(url);
            } catch (err: unknown) {
              toast.error(err instanceof Error ? err.message : "Export failed", { position: 'top-right' });
            }
          }}
          disabled={isExporting || isScanning}
          className="gap-2"
        >
          {isExporting ? (
            <><Loader2 className="h-4 w-4 animate-spin" /><span>Generating...</span></>
          ) : (
            <><FileText className="h-4 w-4" /><span>Export Report</span></>
          )}
        </Button>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="flex-shrink-0">
        <SecurityMetrics metrics={metricsToDisplay} />
      </div>

      {/* Main Content - Takes remaining height */}
      <div className="flex-1 min-h-0">
        <SecurityScannerContent
          metadataItems={metadataItems}
          isLoading={isLoadingFindings}
          severities={securityFiltersResponse?.severities}
          categories={securityFiltersResponse?.categories}
          searchTerm={searchTerm}
          selectedSeverity={selectedSeverity}
          selectedCategory={selectedCategory}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          startItem={startItem}
          endItem={endItem}
          totalFindings={totalFindings}
          totalMetadataItems={totalMetadataItems}
          onSearchChange={handleSearchChange}
          onSeverityChange={handleSeverityChange}
          onCategoryChange={handleCategoryChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onImproveWithAI={handleImproveWithAI}
          isScanning={isScanning}
        />
      </div>
    </div>
  );
};

export const SecurityScannerPage = SecurityScanner;
export default SecurityScanner;